import { useMemo } from 'react';

import { type MessageAction, type MessageWithTextContent } from './types';
import { useCopyMessageText } from './useCopyMessageText';

/** Configuration for which actions should be available. */
type MessageActionPermissions = {
  canReplyInThread?: boolean;
  canReplyInMainChat?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canPin?: boolean;
};

/** Callbacks for each action type. Actions only appear if their callback is provided. */
type MessageActionCallbacks = {
  onReplyInThread?: () => void;
  onReplyInMainChat?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onPin?: () => void;
};

/** Custom labels for actions. Falls back to defaults if not provided. */
type MessageActionLabels = {
  replyInThread?: string;
  replyInMainChat?: string;
  edit?: string;
  delete?: string;
  pin?: string;
  copy?: string;
};

/** Custom icons for actions. Falls back to defaults if not provided. */
type MessageActionIcons = {
  replyInThread?: string;
  replyInMainChat?: string;
  edit?: string;
  delete?: string;
  pin?: string;
  copy?: string;
};

/** State for actions that have pending or toggle states. */
type MessageActionState = {
  isPinPending?: boolean;
};

type UseMessageActionsOptions = {
  /** The message to build actions for. */
  message: MessageWithTextContent & { id: string };
  /** Permission flags controlling which actions are available. */
  permissions?: MessageActionPermissions;
  /** Callbacks for each action. An action only appears if its callback is provided. */
  callbacks?: MessageActionCallbacks;
  /** Custom labels for actions. */
  labels?: MessageActionLabels;
  /** Custom icons for actions. */
  icons?: MessageActionIcons;
  /** State for pending or toggle actions. */
  state?: MessageActionState;
};

type UseMessageActionsResult = {
  /** The complete list of available actions for the message. */
  actions: MessageAction[];
  /** The copy action separately, for cases where it needs special handling. */
  copyAction: MessageAction | null;
};

const DEFAULT_LABELS: Required<MessageActionLabels> = {
  copy: 'Copy text',
  delete: 'Delete message',
  edit: 'Edit message',
  pin: 'Pin',
  replyInMainChat: 'Reply in main chat',
  replyInThread: 'Reply in thread',
};

const DEFAULT_ICONS: Required<MessageActionIcons> = {
  copy: 'ph--copy--regular',
  delete: 'ph--trash--regular',
  edit: 'ph--pencil-simple--regular',
  pin: 'ph--push-pin--regular',
  replyInMainChat: 'ph--arrow-bend-double-up-left--regular',
  replyInThread: 'ph--chat-circle--regular',
};

/**
 * Hook for building a standard set of message actions for chat interfaces.
 * Actions appear based on permissions and whether callbacks are provided.
 * Labels and icons can be customized per-action.
 */
export function useMessageActions(options: UseMessageActionsOptions): UseMessageActionsResult {
  const { message, permissions = {}, callbacks = {}, labels = {}, icons = {}, state = {} } = options;

  const { copyAction: baseCopyAction } = useCopyMessageText(message);

  // Build customized copy action if label or icon overrides exist
  const copyAction = useMemo((): MessageAction | null => {
    if (!baseCopyAction) return null;
    if (!labels.copy && !icons.copy) return baseCopyAction;
    return {
      ...baseCopyAction,
      ...(labels.copy && { label: labels.copy }),
      ...(icons.copy && { icon: icons.copy }),
    };
  }, [baseCopyAction, labels.copy, icons.copy]);

  const actions = useMemo((): MessageAction[] => {
    const result: MessageAction[] = [];

    // Reply in main chat
    if (permissions.canReplyInMainChat && callbacks.onReplyInMainChat) {
      result.push({
        icon: icons.replyInMainChat ?? DEFAULT_ICONS.replyInMainChat,
        label: labels.replyInMainChat ?? DEFAULT_LABELS.replyInMainChat,
        onClick: callbacks.onReplyInMainChat,
      });
    }

    // Reply in thread
    if (permissions.canReplyInThread && callbacks.onReplyInThread) {
      result.push({
        icon: icons.replyInThread ?? DEFAULT_ICONS.replyInThread,
        label: labels.replyInThread ?? DEFAULT_LABELS.replyInThread,
        onClick: callbacks.onReplyInThread,
      });
    }

    // Pin/Unpin
    if (permissions.canPin && callbacks.onPin) {
      result.push({
        icon: icons.pin ?? DEFAULT_ICONS.pin,
        isPending: state.isPinPending,
        label: labels.pin ?? DEFAULT_LABELS.pin,
        onClick: callbacks.onPin,
      });
    }

    // Edit
    if (permissions.canEdit && callbacks.onEdit) {
      result.push({
        icon: icons.edit ?? DEFAULT_ICONS.edit,
        label: labels.edit ?? DEFAULT_LABELS.edit,
        onClick: callbacks.onEdit,
      });
    }

    // Delete
    if (permissions.canDelete && callbacks.onDelete) {
      result.push({
        icon: icons.delete ?? DEFAULT_ICONS.delete,
        label: labels.delete ?? DEFAULT_LABELS.delete,
        onClick: callbacks.onDelete,
        variant: 'destructive',
      });
    }

    // Copy (always last when available)
    if (copyAction) {
      result.push(copyAction);
    }

    return result;
  }, [permissions, callbacks, labels, icons, state, copyAction]);

  return { actions, copyAction };
}
